"use client";

import { ExternalLink, X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";

interface PendingRedirect {
  href: string;
  serviceName?: string;
}

interface ExternalLinkConfirmContextValue {
  confirm: (href: string, serviceName?: string) => void;
}

const ExternalLinkConfirmContext = createContext<ExternalLinkConfirmContextValue | null>(null);

const TRANSITION_MS = 200;

export function ExternalLinkConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingRedirect | null>(null);
  const [visible, setVisible] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useStableId();

  const confirm = useCallback((href: string, serviceName?: string) => {
    setPending({ href, serviceName });
  }, []);

  const closeModal = useCallback(() => {
    setPending(null);
  }, []);

  // Open/close dialog with animation
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (pending && !d.open) {
      d.showModal();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (!pending && d.open) {
      setVisible(false);
      const t = window.setTimeout(() => d.close(), TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
  }, [pending]);

  // Intercept ESC for animated close
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeModal();
    };
    d.addEventListener("cancel", onCancel);
    return () => d.removeEventListener("cancel", onCancel);
  }, [closeModal]);

  const onAgree = () => {
    if (!pending) return;
    window.open(pending.href, "_blank", "noopener,noreferrer");
    closeModal();
  };

  const value = useMemo<ExternalLinkConfirmContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ExternalLinkConfirmContext.Provider value={value}>
      {children}
      <dialog
        ref={dialogRef}
        aria-labelledby={titleId}
        data-visible={visible}
        className={cn(
          "m-0 max-h-none max-w-none border-0 bg-transparent p-0",
          "h-full w-full",
          "backdrop:bg-bg-dark/0 backdrop:backdrop-blur-none backdrop:transition-all backdrop:duration-200 backdrop:ease-out",
          "data-[visible=true]:backdrop:bg-bg-dark/60 data-[visible=true]:backdrop:backdrop-blur-sm",
        )}
      >
        <button
          type="button"
          aria-label="Fechar aviso de link externo"
          tabIndex={-1}
          onClick={closeModal}
          className="fixed inset-0 z-0 cursor-default bg-transparent"
        />

        <div className="pointer-events-none relative z-10 flex min-h-full items-center justify-center p-4">
          <div
            className={cn(
              "pointer-events-auto w-full max-w-md rounded-lg bg-surface p-6 shadow-xl",
              "transform transition-all ease-out will-change-transform",
              visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
            )}
            style={{ transitionDuration: `${TRANSITION_MS}ms` }}
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tint text-primary-hover">
                <ExternalLink aria-hidden className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="text-h6 font-semibold text-fg-strong">
                  Você está sendo direcionado para um link externo
                </h2>
                <p className="mt-2 text-body-sm text-fg-default">
                  O serviço
                  {pending?.serviceName ? (
                    <>
                      {" "}
                      <strong>{pending.serviceName}</strong>
                    </>
                  ) : (
                    ""
                  )}{" "}
                  está hospedado fora do portal <strong>pe.gov.br</strong>. Ao continuar, você sai
                  do portal.
                </p>
                {pending?.href && (
                  <p className="mt-3 break-all rounded-md bg-bg-secondary/60 px-3 py-2 font-mono text-body-xs text-fg-muted">
                    {pending.href}
                  </p>
                )}
              </div>
              <IconButton
                variant="subtle"
                size="sm"
                onClick={closeModal}
                aria-label="Fechar"
                className="shrink-0 text-fg-muted"
              >
                <X aria-hidden className="h-4 w-4" />
              </IconButton>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                className="rounded-pill"
              >
                Cancelar
              </Button>
              <Button type="button" variant="primary" onClick={onAgree} className="rounded-pill">
                Concordo, continuar
              </Button>
            </div>
          </div>
        </div>
      </dialog>
    </ExternalLinkConfirmContext.Provider>
  );
}

export function useExternalLinkConfirm(): ExternalLinkConfirmContextValue["confirm"] {
  const ctx = useContext(ExternalLinkConfirmContext);
  if (!ctx) {
    throw new Error("useExternalLinkConfirm must be used inside ExternalLinkConfirmProvider");
  }
  return ctx.confirm;
}
